import * as  React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import * as _ from 'lodash';
import * as moment from 'moment';
import * as actions from '../../../../redux/projects/actions.projects';
import PopupWrapper from '../../../components/Popup/PopupWrapper';
import ComboBox from '../../../components/ComboBox/ComboBox';
import { TabsComponent } from '../../../components/TabsComponent/Tabs';
import platformNames from '../../../../const/platformNames';
import asyncDispatch from '../../../../services/mini/asyncDispatch';
import AlanButtonStyleForm from './../AlanButtonStyleForm/AlanButtonStyleForm';
import TextChatOptions from './../TextChat/TextChatOptions/TextChatOptions';
import BtnOptionsCommentItem from './../BtnOptionsCommentItem/BtnOptionsCommentItem';
import { IStore } from '../../../../redux/initialState';
import { useCallback, useState } from 'react';
import NewButtonOptionsForm from '../NewButtonOptionsForm/NewButtonOptionsForm';
import { getProjectOptionsByEnvironment } from '../../../../services/mini/getProjectOptionsByEnvironment';
import ToasterService from '../../../../services/ToasterService';
import ConfirmPopup from '../../../components/ConfirmPopup/ConfirmPopup';
import { getDefaultOptions } from '../defaultProjectOptions';
import isLocalStorageAvailable from '../../../../services/mini/isLocalStorageAvailable';
import { Loader, LoaderSize, LoaderTheme } from '../../../components/Loader/Loader';
import { getBtnStyles, getSavedOptions, getSavedOptionsPerPlatform, getSelectedBtnStyleId } from './btnStylesHelper';

const VALUES_TO_EXPORT_IMPORT = [
    'logoIdle',
    'logoListen',
    'logoProcess',
    'logoReply',
    'logoTextChat',
    'logoTextChatAvatar',
    'logoTextChatUserAvatar',
    'logoUrl',
    'btnOptions',
    'chatOptions',
    'buttonSize',
];

type SelectedTab = 'button' | 'textChat';

const TABS = [
    { name: 'Alan Button', id: 'button' },
    { name: 'Text Chat', id: 'textChat' },
];

const THEMES = [
    { name: 'Default', id: 'default' },
    { name: 'Light', id: 'light' },
    { name: 'Dark', id: 'dark' },
];

function VisualStyleOptions(props) {
    const {
        onClose,
        platform,
        environment,
        cohorts,
        selectedCohortId,
        project,
        getButtonOptions,
        addButtonOptions,
        setProjectOptions,
        deleteButtonOptions,
        isProjectOptionsSaving
    } = props;

    const [tab, setTab] = useState<SelectedTab>('button');
    const [selectedBtnStyleId, setSelectedBtnStyleId] = useState<string>(getSelectedBtnStyleId(props));
    const [btnStyles, setBtnStyles] = useState<any[]>([]);
    const [showSaveBtnOptionsStyleDialog, setShowSaveBtnOptionsStyleDialog] = useState(false);
    const [showOnBackBtnDialog, setShowOnBackBtnDialog] = useState(false);

    const [btnStyleToDelete, setBtnStyleToDelete] = useState(null);
    const [btnStyleToChange, setBtnStyleToChange] = useState(null);

    const { projectId } = project;

    const [optionsPerPlatform, setOptionsPerPlatform] = useState(null);
    const [updateTs, setUpdateTs] = useState(null);

    const hasChanges = () => {
        return JSON.stringify(getSavedOptionsPerPlatform(props)) !== JSON.stringify(optionsPerPlatform);
    };

    const hasUnsavedChanges = hasChanges();

    const saveOptions = (selectedBtnOptionId, optionsToSave) => {
        const options = getSavedOptions(props);
        options[platform] = {
            ...optionsToSave,
            selectedBtnOptionId: selectedBtnOptionId
        };

        setOptionsPerPlatform({ ...options[platform] });

        setProjectOptions({ project, options, environment, cohorts, selectedCohortId }).then(() => {
            setSelectedBtnStyleId(selectedBtnOptionId);
            setUpdateTs(Date.now());
        });
    };

    const changeBtnOption = (id, selectedBtnStyle) => {
        if (id === 'default') {
            resetToDefault();
            return;
        }
        if (selectedBtnStyle.options) {
            const optionsToSave = JSON.parse(selectedBtnStyle.options);
            saveOptions(id, getOptionsWithTheme(optionsToSave));
        }
    };

    const handleChangeBtnOptionsStyle = ({ id }) => {
        if (selectedBtnStyleId !== id) {
            const selectedBtnStyle = _.find(btnStyles, { id });

            if (hasChanges()) {
                setBtnStyleToChange(selectedBtnStyle);
            } else {
                changeBtnOption(id, selectedBtnStyle);
            }
        }
    };

    React.useEffect(() => {
        getButtonOptions({ projectId }).then((options) => {
            setBtnStyles(getBtnStyles(options));
            setOptionsPerPlatform(getSavedOptionsPerPlatform(props));
        });
    }, []);

    const handleClose = () => {
        if (hasChanges()) {
            setShowOnBackBtnDialog(true);
            return;
        }

        onClose();
    };

    const saveBtnOptionsStylePromiseResolve = React.useRef({});
    const fileInputRef = React.createRef<HTMLInputElement>();

    const saveBtnOptionsStyle = () => {
        return new Promise((resolve, reject) => {
            saveBtnOptionsStylePromiseResolve.current = resolve;
            setShowSaveBtnOptionsStyleDialog(true);
        });
    };

    const resetToDefault = () => {
        const defaultOptions = getDefaultOptions();
        saveOptions('default', getOptionsWithTheme(defaultOptions));
    };

    const getOptionsWithTheme = (options) => {
        return {
            ...options,
            ...(optionsPerPlatform.themes ?
                { themes: optionsPerPlatform.themes } : {})
        }
    }

    const handleExportSettings = () => {
        const optionsToExport = {};
        const curBtnStyle = _.find(btnStyles, { id: selectedBtnStyleId });
        const projectName = `${project.name?.replace(/ /g, '_')}${project.name?.toLowerCase().indexOf('project') === -1 ? '_project' : ''}`;
        const styleName = curBtnStyle ? `${curBtnStyle.name}${curBtnStyle.name?.toLowerCase().indexOf('style') === -1 ? '_style' : ''}`.replace(/ /g, '_') : '';
        const ts = moment().format('YYYY_MM_DD__HH_mm_ss');

        for (let i = 0; i < VALUES_TO_EXPORT_IMPORT.length; i++) {
            const key = VALUES_TO_EXPORT_IMPORT[i];
            optionsToExport[key] = optionsPerPlatform[key];
        }

        if (isLocalStorageAvailable()) {
            ToasterService.showInfo({ title: 'Options successfully exported!' });
            const fileName = `${projectName}__${platform}_platform__${styleName}__settings__${ts}.json`;
            downloadJsonFile(optionsToExport, fileName.toLowerCase());
        }
    };

    const handleImportSettings = () => {
        fileInputRef.current.click();

    };

    const handleOnFileChanged = () => {

        const fileInput = document.getElementById('fileInput');
        const file = (fileInput as any).files[0];

        if (!file) {
            console.error('No file selected.');
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const content = event.target.result;
                const contentJson = JSON.parse(content as string);
                const options = getSavedOptions(props);

                for (let i = 0; i < VALUES_TO_EXPORT_IMPORT.length; i++) {
                    const key = VALUES_TO_EXPORT_IMPORT[i];
                    if (contentJson[key]) {
                        const savedVal = contentJson[key];
                        if (_.isArray(savedVal)) {
                            options[platform][key] = [...contentJson[key]];
                        } else if (_.isObject(savedVal)) {
                            options[platform][key] = { ...contentJson[key] };
                        } else {
                            options[platform][key] = contentJson[key];
                        }
                    }
                }

                setOptionsPerPlatform(options[platform]);
                setUpdateTs(Date.now());
                ToasterService.showInfo({ title: 'Options successfully imported! Save this button styles to apply them to the Alan Button' });
            } catch (error) {

            }
        };
        reader.readAsText(file);
    };

    return <PopupWrapper className="visual-style alan-button-options">
        <div className="visual-style_external-wrapper">
            <div className="popup-form_header popup-form_header_with-commands">
                <div className={`popup-form_header_cmd`}
                    onClick={handleClose}>
                    <i className="icon icon-alan-chevron"
                        style={{
                            transform: 'rotate(90deg)',
                            display: 'inline-block',
                            marginRight: '5px'
                        }} />
                    Back
                </div>
                <div>{`${platformNames[platform]} - Visual Styles`}</div>
                <div className="visual-style_header-btns">
                    <span title="Export Settings"
                        className="action-btn light-blue-with-border visual-style_copy-settings-btn" onClick={handleExportSettings}>  Export</span>
                    <span title="Import Settings" className={`action-btn light-blue-with-border visual-style_paste-settings-btn`} onClick={handleImportSettings}>Import</span>
                    <input style={{ display: 'none' }} type="file" id="fileInput" ref={fileInputRef} onChange={handleOnFileChanged} accept=".json" />
                </div>
            </div>
            <div className="popup-form_internal-wrapper visual-style_internal-wrapper">
                <div className="visual-style_body">
                    <div className="visual-style_block-header">
                        <div className="visual-style_style-name-block">
                            <div>Style Name</div>
                            <ComboBox
                                values={btnStyles}
                                valueKey="id"
                                valueName="name"
                                classes={`visual-style_saved-options-combo-box ${btnStyles.length === 1 && 'disabled'}`}
                                selectedValueId={selectedBtnStyleId}
                                onValueSelect={handleChangeBtnOptionsStyle}
                                renderItem={(optionsItem, inList, closeComboBoxCb) => {
                                    return <BtnOptionsCommentItem
                                        key={optionsItem.id}
                                        onDelete={(optionsItem, e) => {
                                            setBtnStyleToDelete(optionsItem);
                                            closeComboBoxCb(e);
                                        }}
                                        project={project}
                                        platform={platform}
                                        optionsItem={optionsItem} />;
                                }} />
                        </div>
                        <div className={`action-btn  save-options-btn ${(!hasUnsavedChanges || isProjectOptionsSaving) && 'disabled'}`}
                            onClick={() => setShowSaveBtnOptionsStyleDialog(true)}>
                            Save
                        </div>
                    </div>
                    <TabsComponent
                        valueName="name"
                        valueKey="id"
                        tabs={TABS}
                        currentTabId={tab}
                        onChange={({ id }) => { setTab(id); }} />
                    <div className="visual-style_tab-holder">
                        {!optionsPerPlatform &&
                            <Loader
                                size={LoaderSize.Medium}
                                theme={LoaderTheme.Dark}
                                center={true}
                                position="absolute" />}
                        {(tab === 'button' && optionsPerPlatform) &&
                            <AlanButtonStyleForm
                                key={updateTs}
                                currentOptions={optionsPerPlatform}
                                platform={platform}
                                environment={environment}
                                projectId={project.projectId}
                                project={project}
                                cohorts={cohorts}
                                selectedCohortId={selectedCohortId}
                                onOptionsChange={(newOptions) => {
                                    setOptionsPerPlatform(newOptions);
                                }} />}
                        {(tab === 'textChat' && optionsPerPlatform) &&
                            <TextChatOptions
                                key={updateTs}
                                currentOptions={optionsPerPlatform}
                                platform={platform}
                                environment={environment}
                                projectId={project.projectId}
                                project={project}
                                cohorts={cohorts}
                                selectedCohortId={selectedCohortId}
                                onOptionsChange={(newOptionsPerPlatform) => {
                                    setOptionsPerPlatform(prev => {
                                        return {
                                            ...prev,
                                            chatOptions: {
                                                textChat: { ...(newOptionsPerPlatform as any)?.chatOptions?.textChat || {} }
                                            }
                                        };
                                    });
                                }}
                            />
                        }
                    </div>
                </div>
            </div>
        </div>

        {(showOnBackBtnDialog || btnStyleToChange?.id || btnStyleToDelete?.id || showSaveBtnOptionsStyleDialog) &&
            <div className="alan-button-style-form_overlay">

                {showSaveBtnOptionsStyleDialog &&
                    <NewButtonOptionsForm
                        onClose={() => {
                            setShowSaveBtnOptionsStyleDialog(false);
                        }}
                        addButtonOptions={({ name }) => {
                            var optionsWoThemes = { ...optionsPerPlatform };
                            delete optionsWoThemes['themes'];

                            addButtonOptions({
                                name,
                                projectId: project.projectId,
                                options: JSON.stringify(optionsWoThemes)
                            }).then((data) => {
                                setBtnStyles(getBtnStyles(data.options));
                                setSelectedBtnStyleId(data.current.id);
                                const options = getSavedOptions(props);

                                options[platform] = {
                                    ...optionsPerPlatform,
                                    selectedBtnOptionId: data.current.id
                                };

                                setProjectOptions({ project, options, environment, cohorts, selectedCohortId }).then(() => {
                                    ToasterService.showInfo({ title: 'Options saved' });
                                    setOptionsPerPlatform({ ...options[platform] });
                                    setUpdateTs(Date.now());
                                    if (_.isFunction(saveBtnOptionsStylePromiseResolve.current)) {
                                        (saveBtnOptionsStylePromiseResolve.current as any)('saved');
                                    }
                                });
                            });
                        }}
                        savedOptions={[{ id: 'default', name: 'Custom style' }, ...btnStyles.slice(1)]}
                        project={project} />}

                {btnStyleToDelete?.id && <ConfirmPopup
                    externalLayerClass="inner-overlay"
                    onClose={(e) => {
                        setBtnStyleToDelete(null);
                    }}
                    okBtnLabel="Delete"
                    okBtnCb={() => {
                        setBtnStyleToDelete(null);

                        if (optionsPerPlatform?.themes?.light?.id === btnStyleToDelete.id) {
                            ToasterService.showInfo({ title: `The style you want to delete is currently being used in the Light Theme. Please detach it before deleting the style.` });
                            return;
                        }

                        if (optionsPerPlatform?.themes?.dark?.id === btnStyleToDelete.id) {
                            ToasterService.showInfo({ title: `The style you want to delete is currently being used in the Light Theme. Please detach it before deleting the style.` });
                            return;
                        }

                        deleteButtonOptions({
                            projectId: project.projectId,
                            id: btnStyleToDelete.id
                        }).then((options) => {
                            if (selectedBtnStyleId === btnStyleToDelete.id) {
                                setSelectedBtnStyleId('default');
                                resetToDefault();
                            }
                            setBtnStyles(getBtnStyles(options));
                        });
                    }}
                    title="Delete Version"
                    message={<div>Are you sure you want to delete <b>{btnStyleToDelete.name}</b>?</div>} />}


                {btnStyleToChange?.id && <ConfirmPopup
                    externalLayerClass="inner-overlay"
                    onClose={() => {
                        setBtnStyleToChange(null);
                    }}
                    midBtnLabel="Switch without saving"
                    midBtnCb={() => {
                        changeBtnOption(btnStyleToChange?.id, btnStyleToChange);
                        setBtnStyleToChange(null);
                    }}
                    midBtnCbClass="cancel-action light-blue-with-border"
                    midBtnWidth={160}
                    okBtnLabel="Save changes & switch"
                    okBtnCb={() => {
                        setBtnStyleToChange(null);

                        saveBtnOptionsStyle().then((status) => {
                            if (status === 'saved') {
                                setTimeout(() => {
                                    changeBtnOption(btnStyleToChange?.id, btnStyleToChange);
                                });
                            }
                        });
                    }}
                    okBtnWidth={160}
                    title="Switch Without Saving"
                    message={<div>Do you want to save changes in current style before
                        switching to <b>{btnStyleToChange?.name}</b>?</div>} />}

                {showOnBackBtnDialog && <ConfirmPopup
                    externalLayerClass="save-options-confirm-dialog"
                    onClose={() => {
                        setShowOnBackBtnDialog(false);
                    }}
                    midBtnLabel="Leave without saving"
                    midBtnCb={() => {
                        onClose();
                    }}
                    midBtnCbClass="cancel-action light-blue-with-border"
                    midBtnWidth={160}
                    okBtnLabel="Save changes & leave"
                    okBtnCb={() => {
                        setShowOnBackBtnDialog(false);
                        saveBtnOptionsStyle().then((status) => {
                            if (status === 'saved') {
                                onClose();
                            }
                        });
                    }}
                    okBtnWidth={160}
                    title="Leave Without Saving"
                    message="Do you want to save changes before leaving?" />}
            </div>}
    </PopupWrapper>;
}


function mapStateToProps({ projectsInfo, user }: IStore) {
    return {
        isProjectOptionsSaving: projectsInfo.isProjectOptionsSaving,
        email: user.email
    };
}

function mapDispatchToProps(dispatch) {
    return {
        setProjectOptions: (param) => asyncDispatch(actions.setProjectOptions, dispatch, param),
        getButtonOptions: (param) => asyncDispatch(actions.getButtonOptions, dispatch, param),
        addButtonOptions: (param) => asyncDispatch(actions.addButtonOptions, dispatch, param),
        deleteButtonOptions: (param) => asyncDispatch(actions.deleteButtonOptions, dispatch, param),
        getProjectDetail: (param) => asyncDispatch(actions.getProjectInfo, dispatch, param),
    };
}

function downloadJsonFile(data, filename) {
    const blob = new Blob([JSON.stringify(data, null, 4)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(VisualStyleOptions);

